import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Box, Paper, Typography } from '@mui/material';
import {
  Notifications as AlertIcon,
  Campaign as BroadcastIcon,
  Settings as DefaultIcon,
  Email as EmailIcon,
  Event as EventIcon,
  PersonAdd as JoinIcon,
  Handshake as MeetingIcon,
  Message as MessageIcon,
  MarkEmailRead as OpenIcon,
  Phone as PhoneIcon,
  Draw as SignatureIcon,
  Sms as SmsIcon,
  Report as SpamIcon,
  ThumbDown as ThumbDownIcon,
  Unsubscribe as UnsubscribedIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledPaper = styled(Paper)`
  box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
  margin: 4px 0;
  border-left: 4px solid ${props => props.categorycolor};
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  transition: transform 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 4px 16px rgb(0 0 0 / 15%);
    transform: translateY(-2px);
  }
`;

const StyledTimelineDot = styled(TimelineDot)`
  box-shadow: 0 2px 8px rgb(0 0 0 / 20%);
  border: 2px solid white;
  background-color: ${props => props.categorycolor};
`;

const ActivityItem = ({ label, category, action, date }) => {
  const dateObj = new Date(date);
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const formattedTime = dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Define colors and icons based on category and action
  const getCategoryConfig = (categoryType, actionType) => {
    const getIcon = (cat, act) => {
      if (cat === 'broadcast') {
        if (act === 'email') return <EmailIcon />;

        if (act === 'open') return <OpenIcon />;

        if (act === 'sms') return <SmsIcon />;

        if (act === 'bounced') return <ThumbDownIcon />;

        if (act === 'unsubscribed') return <UnsubscribedIcon />;

        if (act === 'spam') return <SpamIcon />;

        return <BroadcastIcon />;
      }

      if (cat === 'action') {
        if (act === 'phone_call') return <PhoneIcon />;

        if (act === 'message') return <MessageIcon />;

        if (act === 'signature') return <SignatureIcon />;

        return <EventIcon />;
      }

      if (cat === 'meeting') return <MeetingIcon />;

      if (cat === 'join') return <JoinIcon />;

      if (cat === 'alert') return <AlertIcon />;

      return <DefaultIcon />;
    };

    const configs = {
      action: {
        color: '#7b1fa2',
        icon: getIcon(categoryType, actionType),
      },
      alert: {
        color: '#f57c00',
        icon: getIcon(categoryType, actionType),
      },
      broadcast: {
        color: '#1976d2',
        icon: getIcon(categoryType, actionType),
      },
      default: {
        color: '#757575',
        icon: getIcon(categoryType, actionType),
      },
      join: {
        color: '#388e3c',
        icon: getIcon(categoryType, actionType),
      },
      meeting: {
        color: '#00796b',
        icon: getIcon(categoryType, actionType),
      },
    };

    return configs[categoryType] || configs.default;
  };

  const config = getCategoryConfig(category, action);

  const getActionLabel = (categoryType, actionType) => {
    const actionLabels = {
      action: {
        default: 'Action taking (advocacy)',
        message: 'Contact sent an action message',
        phone_call: 'Contact made an action phone call',
        signature: 'Contact signed an action',
      },
      alert: {
        default: 'Action alerts',
      },
      broadcast: {
        bounced: 'Email bounced',
        default: 'Broadcast messages',
        email: 'Contact was sent an email',
        open: 'Contact opened an email',
        sms: 'Contact was sent a text message (SMS)',
        spam: 'Contact marked email spam',
        unsubscribed: 'Contact unsubscribed (email)',
      },
      join: {
        default: 'Joined list',
        join: 'Contact joined list',
      },
      meeting: {
        default: 'Meeting',
        met: 'Attended a meeting',
      },
    };

    return (
      actionLabels[categoryType]?.[actionType] ||
      actionLabels[categoryType]?.default ||
      `${categoryType} ${actionType}`
    );
  };

  return (
    <TimelineItem>
      <TimelineOppositeContent
        sx={{
          flex: 0,
          m: 'auto 0',
          minWidth: 120,
          pr: 2,
          textAlign: 'right',
        }}
        align="left"
        variant="body2"
        color="text.secondary"
      >
        <Typography variant="body2" fontWeight="bold">
          {formattedDate}
        </Typography>
        <Typography variant="caption">{formattedTime}</Typography>
      </TimelineOppositeContent>

      <TimelineSeparator>
        <StyledTimelineDot categorycolor={config.color}>{config.icon}</StyledTimelineDot>
        <TimelineConnector />
      </TimelineSeparator>

      <TimelineContent
        sx={{
          px: 2,
          py: '12px',
        }}
      >
        <StyledPaper categorycolor={config.color} elevation={0}>
          <Typography variant="h6" component="h3" gutterBottom>
            {getActionLabel(category, action)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Box mt={1}>
            <Typography
              variant="caption"
              sx={{
                backgroundColor: `${config.color}20`,
                borderRadius: '12px',
                color: config.color,
                fontWeight: 'bold',
                padding: '2px 8px',
              }}
            >
              {category.toUpperCase()}
            </Typography>
          </Box>
        </StyledPaper>
      </TimelineContent>
    </TimelineItem>
  );
};

ActivityItem.propTypes = {
  action: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

export default ActivityItem;
